import { useFeatureFlag } from '@shape/feature-flag';
import JSZip from 'jszip';
import { useBatchDownloadFilesQuery, useBatchListFilesQuery } from 'queries';
import { OverviewAsset } from 'queries/assets/contract';
import { useEffect, useState } from 'react';

export interface IImage {
  name: string;
  src: Promise<Blob>;
  resolvedSrc: string;
  files: string[];
  id?: string;
}
interface IFile {
  audit_log: {
    action_date: string;
    action_name: string;
    file_name: string;
    user_name: string;
  }[];
  files: string[];
  key_name: string;
  key_type: string;
}

export const useBatchDownload = (
  assets: { name: string; id: string; [key: string]: any }[],
  keyType?: string,
) => {
  const isDemoOTCEnabled = useFeatureFlag('demo-OTC');
  const [batch_list, setBatchList] = useState<any>({
    batch_list: [],
    queryKey: [],
  });
  const [assetImages, setAssetImages] = useState<IImage[]>();

  const { data: files } = useBatchListFilesQuery({
    batch_list: assets.map((a) => ({
      key_name: String(a.id),
      key_type: keyType ?? 'asset_image_by_asset_id',
      show_audit_log: true,
    })),
    assets,
  });

  const { data: download } = useBatchDownloadFilesQuery(batch_list);

  const getImageForAsset = (assetId: string) => {
    const splitKey = isDemoOTCEnabled ? '/' : '||';
    return assetImages?.find(
      (image) => image.name.split(splitKey)[1] === String(assetId) || image.id === assetId,
    );
  };

  const getFileName = (auditLog: { action_name: string; file_name: string }[]) => {
    const log = auditLog.reverse().find((item) => item.action_name === 'INSERT');
    return log ? log.file_name : '';
  };

  useEffect(() => {
    if (files && files?.data?.batch_list) {
      const temp = {
        batch_list: files?.data?.batch_list
          .filter((file: IFile) => file.files.length && file.audit_log.length)
          .map(({ key_name, key_type, audit_log }: IFile) => ({
            key_name,
            key_type,
            file_name: getFileName(audit_log),
          })),
        queryKey: assets,
      };
      setBatchList(temp);
    }
  }, [files]);

  const resolvePromiseToImgSrc = async (files: IImage[]) => {
    for (const file of files) {
      file.resolvedSrc = URL.createObjectURL(await file.src);
    }
    setAssetImages(files);
  };

  useEffect(() => {
    const imagePromises: IImage[] = [];
    if (download?.data) {
      const zip = new JSZip();
      zip.loadAsync(download.data).then((zip) => {
        Object.keys(zip.files).forEach((fileName) => {
          if (zip.files[fileName].dir) {
            return;
          }

          const parts = fileName.split('/');
          const key = isDemoOTCEnabled ? parts[parts.length - 2] : fileName.split('||')[1];
          const file = files.data?.batch_list.find(
            (d: { key_name: string; generic_key_name?: string }) =>
              d.key_name === key || d.generic_key_name === key,
          );

          if (!file) {
            console.warn('Arquivo não encontrado para keyNameFromPath:', key);
            return;
          }

          imagePromises.push({
            name: fileName,
            src: zip.files[fileName].async('blob'),
            resolvedSrc: '',
            files: file.files,
            id: file.key_name,
          });
        });

        resolvePromiseToImgSrc(imagePromises);
      });
    }
  }, [download?.data]);

  return {
    assetImages,
    getImageForAsset,
  };
};
